import React, { useEffect, useState } from 'react';
import { useAuthState } from '../../auth/authContext';
import { useFanState } from '../../fanContext/fanContext';
import { ScrollView } from 'react-native-gesture-handler';
import Constants from 'expo-constants';
import * as WebBrowser from 'expo-web-browser';
import client from '../../../graphql/client';
import useSpotifyAuth from '../../hooks/useSpotifyAuth';
import Monitor, { FingerPrint } from '../../lib/monitoring/Monitor';
import SpotifyWebApi from '../../api/spotifyWebApi';
import * as LocalStorage from '../../lib/LocalStorage';
import ConnectToSpotify from '../../components/styled/ConnectToSpotify';
import FeedbackBox from '../../components/styled/FeedbackBox';
import HeaderOne from '../../components/styled/HeaderOne';
import SpotifyPlayer from '../../components/styled/SpotifyPlayer';
import SpotifyPlayerError, {
  SpotifyErrorCodes,
} from '../../components/styled/SpotifyPlayerError';
import View from '../../components/styled/View';
import SplashScreen from '../../components/styled/SplashScreen';
import styles from './styles';
import { UriInfo } from '../../types/types';
import GalanoGrotesqueText from '../../components/styled/GalanoGrotesqueText';
import UserError, { UserErrorCodes } from '../../components/styled/UserError';
import { NO_MORE_TRACKS_TEXT, NO_TRACKS_HEADER } from './constants';
import { useKeepAwakeWeb } from '../../hooks/useKeepAwakeWeb';
import UserOnboarding from '../../components/styled/UserOnboarding';

// Has to exist in the location where the spotify pop up login screen gets called in order to close it on success
WebBrowser.maybeCompleteAuthSession();

export default function ListenScreen({ navigation }) {
  useKeepAwakeWeb();

  const { cognitoId, email } = useAuthState();
  const { fanGenres: updatedFanGenres } = useFanState();
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // SPOTIFY STATE
  const [matchedTracks, setTracks] = useState([]);
  const [queuedSessions, setQueuedSessions] = useState([]);
  const [currentSession, setCurrentSession] = useState<any>();
  const [showConnectToSpotify, setShowConnectToSpotify] = useState<boolean>(
    true,
  );
  const [spotifyError, setSpotifyError] = useState<boolean>(false);
  const [updatedSpotifyId, setUpdatedSpotifyId] = useState<string>('');

  // FAN STATE
  const [fanError, setFanError] = useState<keyof typeof UserErrorCodes>(
    UserErrorCodes.NONE,
  );
  const [isReturningFan, setIsReturningFan] = useState<boolean>(false);
  const [showOnboardingFlow, setShowOnboardingFlow] = useState<boolean>(true);
  // ==== Feedback and Spotify function pass ups to pass back down to the opposite component
  const [feedbackIsComplete, setFeedbackComplete] = useState<boolean>(false);
  const [currentPlayerUriObj, setCurrentPlayerUri] = useState<UriInfo>({
    id: '',
    linkedFromId: '',
  });

  const {
    isAuthenticatedViaSpotify,
    error: spotifyAuthError,
    authenticateAsync,
    renewAuth,
  } = useSpotifyAuth();

  const readyToRenderFeedback =
    currentPlayerUriObj.id !== '' && currentSession?.id;

  const getFan = async () => {
    //TODO: handle if a fan can't be found
    const rawFanInfo = await client.getFanData(cognitoId);
    return rawFanInfo;
  };

  const getFanGenres = (fanData) => {
    const fanFavGenres = fanData?.favoriteGenres?.items?.map((fanGenre) => {
      return {
        id: fanGenre.genre.id,
        genre: fanGenre.genre.superGenre,
      };
    });
    return fanFavGenres;
  };

  const getMatchedTracks = async () => {
    try {
      const fanData = await getFan();
      if (!fanData) {
        const message = 'Fan profile error';
        const error = "Error in fanListen page, user doesn't have a profile";
        Monitor.notify(error, setMonitorData(message));
        setFanError(UserErrorCodes.FAN_LISTEN_ERROR);
        return;
      }
      const fanGenres = getFanGenres(fanData);
      const fanFeedback = fanData?.allFeedback?.items;
      let allSpotifyIds: string[] = [];
      let allSessions: any[] = [];
      // If genres are not null and length is greater than one then get the songs from the genres
      if (fanGenres && fanGenres.length > 0) {
        setFanError(UserErrorCodes.NONE);
        for (let genre of fanGenres) {
          const sessionData = await client.getSongsByGenre(genre.genre);
          const activeSessions = sessionData.filter(
            (session) => session?.isActive == true,
          );
          allSessions.push(...activeSessions);
        }
        if (fanFeedback.length > 0) {
          const filteredSessions = allSessions
            .map((session) => {
              const feedbackExist = fanFeedback.find(
                (feedback) => feedback.sessionId === session.id,
              );
              if (!feedbackExist) {
                return session;
              }
            })
            .filter((x) => x);
          // @ts-ignore
          allSessions = filteredSessions;
        }

        let sortedSessions = allSessions.sort((a, b) =>
          b.createdAt.localeCompare(a.createdAt),
        );

        // ======================================================================
        // TODO: Remove after private beta: https://app.shortcut.com/tone-birds/story/471/remove-genre-restriction-for-fans-after-private-beta
        const isBetaEnv = Constants.manifest.extra.environment === 'beta';
        if (isBetaEnv) {
          // only choose the sessions from the adamstarrfarish+TestFan@gmail.com test artist
          sortedSessions = sortedSessions.filter(
            (x) => x.artistId === 'c3f2dd5a-299f-4380-87be-1ea4bc1fc488',
          );
        }
        // ======================================================================

        setQueuedSessions(sortedSessions);

        allSpotifyIds = sortedSessions.map((session) => session.spotifyUri);
        if (allSpotifyIds.length > 0) {
          const listOfTracks = await SpotifyWebApi.getMultipleTracks(
            allSpotifyIds,
          );
          const filteredTracks = listOfTracks.tracks.filter(
            (actualTrack) => actualTrack,
          ); //filters out any null tracks in the event its incorrect
          setTracks(filteredTracks);
          setIsReturningFan(fanData.isReturningFan);
        }
      } else {
        const message = 'Fan Genre Error';
        const error = "Error in fanlisten page, user doesn't have genres set";
        Monitor.notify(error, setMonitorData(message));
        setFanError(UserErrorCodes.FAN_LISTEN_ERROR);
      }
    } catch (error) {
      // TODO: set a user facing error, right now the user will just see no matched tracks even though there might be an error
      const message = 'An await function threw an error in fan listen page';
      Monitor.notify(error, setMonitorData(message));
      console.error('Error in await function: ', error);
    }
  };

  // This function will be called if the playcount changes or the spotifyid has been updated
  const updateQueuedSessions = (newSession) => {
    const clonedQueuedSessions = queuedSessions.map((session) => {
      if (session.id === newSession.id) {
        return newSession;
      }
      return session;
    });
    // @ts-ignore
    setQueuedSessions(clonedQueuedSessions);
  };
  const sendSessionPlayCount = async () => {
    const newPlayCount = currentSession?.playCount + 1;
    const newSession = await client.updateSessionPlayCount(
      currentSession.id,
      newPlayCount,
    );
    updateQueuedSessions(newSession);
  };

  const setPlayerUriAndCurrentSession = (uriObj) => {
    let shouldSpotifyIdBeUpdated = false;
    const currentSession = queuedSessions.find((session) => {
      const parsedUri = session.spotifyUri;
      let newSession = uriObj.id == parsedUri;
      if (!newSession && uriObj.linkedFromId) {
        newSession = uriObj.linkedFromId == parsedUri;
        shouldSpotifyIdBeUpdated = true;
      }
      return newSession;
    });
    setCurrentSession(currentSession); // The order here matters current session must be set before player uri
    setCurrentPlayerUri(uriObj);
    shouldSpotifyIdBeUpdated && setUpdatedSpotifyId(uriObj.id);
  };

  const setMonitorData = (message) => {
    return {
      context: { user_id: cognitoId, user_type: 'fan', user_email: email },
      name: 'FanListen',
      fingerprint: FingerPrint.fanListen,
      message,
    };
  };

  useEffect(() => {
    async function determineUserSpotifyState() {
      if (spotifyAuthError) {
        setSpotifyError(spotifyAuthError);
        return;
      }
      const credentials = await LocalStorage.getAuthCredentialsAsync();
      const tokenExpirationTime = credentials.expirationTime;
      if (!tokenExpirationTime) {
        setShowConnectToSpotify(true);
        setIsLoading(false);
      }

      if (tokenExpirationTime) {
        await renewAuth();
        setSpotifyError(spotifyAuthError);
        if (matchedTracks.length === 0 && isAuthenticatedViaSpotify) {
          setIsLoading(true);
          await getMatchedTracks();
          setShowConnectToSpotify(false);
          setIsLoading(false);
        }
      }
    }
    determineUserSpotifyState();
  }, [isAuthenticatedViaSpotify, spotifyAuthError]);

  useEffect(() => {
    // FIXME: EFFECT to refetch matched tracks after fanGenres have been updated, FIX once Apollo client is used
    async function refetchTracks() {
      if (isAuthenticatedViaSpotify) {
        setTracks([]);
        setFanError(UserErrorCodes.NONE);
        setIsLoading(true);
        await getMatchedTracks();
        setIsLoading(false);
      }
    }
    refetchTracks();
    setPlayerUriAndCurrentSession({ id: '', linkedFromId: '' });
  }, [updatedFanGenres]);

  useEffect(() => {
    if (updatedSpotifyId) {
      updateCurrentSpotifyId();
    }
    async function updateCurrentSpotifyId() {
      const newSession = await client.updateSessionSpotifyId(
        updatedSpotifyId,
        currentSession?.id,
      );
      updateQueuedSessions(newSession);
      setUpdatedSpotifyId('');
    }
  }, [updatedSpotifyId]);

  if (fanError !== UserErrorCodes.NONE) {
    return (
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <UserError errorType={fanError} navigation={navigation} />
      </ScrollView>
    );
  }
  if (spotifyError) {
    return (
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.container}>
          <SpotifyPlayerError
            errorType={SpotifyErrorCodes.GENERAL_AUTHENTICATION}
            handleSpotifyErrorReset={authenticateAsync}
          />
        </View>
      </ScrollView>
    );
  }

  if (!isLoading && !isReturningFan && showOnboardingFlow) {
    return (
      <ScrollView contentContainerStyle={styles.contentContainer}>
        <View style={styles.container}>
          <UserOnboarding setShowOnboardingFlow={setShowOnboardingFlow} />
        </View>
      </ScrollView>
    );
  }

  return (
    <ScrollView contentContainerStyle={styles.contentContainer}>
      <View style={styles.container}>
        {/* When data is fetching first render */}
        {isLoading && (
          <SplashScreen text={'Loading your player experience..'} />
        )}
        {/* When user does not have a valid spotify session */}
        {!isLoading && showConnectToSpotify && (
          <ConnectToSpotify onPress={authenticateAsync} />
        )}
        {/* When a user comes back to the page and refresh tokens need to be called  */}
        {/* When user has all token necessary render the player */}
        {!isLoading && !showConnectToSpotify && (
          <View>
            {matchedTracks.length > 0 && (
              <SpotifyPlayer
                fanId={cognitoId}
                isReturningFan={isReturningFan}
                feedbackIsComplete={feedbackIsComplete}
                handleCurrentPlayerUri={setPlayerUriAndCurrentSession}
                navigation={navigation}
                trackList={matchedTracks}
                updatePlayCount={sendSessionPlayCount}
              />
            )}
            {matchedTracks.length === 0 && (
              <View>
                <HeaderOne title={NO_TRACKS_HEADER} />
                <GalanoGrotesqueText style={styles.platformText}>
                  {NO_MORE_TRACKS_TEXT}
                </GalanoGrotesqueText>
              </View>
            )}
            {/* TODO: Set this to render when the spotify player has sent back an all green and pass in the uri info */}
            {!!readyToRenderFeedback && (
              <FeedbackBox
                fanId={cognitoId}
                isReturningFan={isReturningFan}
                currentSessionId={currentSession?.id}
                feedbackComplete={feedbackIsComplete}
                handleFeedbackComplete={setFeedbackComplete}
                uriInfo={currentPlayerUriObj}
              />
            )}
          </View>
        )}
      </View>
    </ScrollView>
  );
}
